<template>
  <li
    class="console-completion"
    :class="{ noCompletionHelpers, selected }"
    @click="onClick"
    @mouseover="onMouseover"
  >
    <div class="left-text">
      <CalendarColorBar
        v-if="calendarColorBar && !isEvent"
        :color="calendarColorBar"
        class="calendar-bar"
      />
      <TypeIcon
        v-else-if="asType || objectType"
        :type="asType ?? objectType"
        class="icon object-emoji"
      />
      <TaskCheckbox
        v-else-if="taskStatus"
        :status="taskStatus"
        :is-interactive="false"
        size="md"
        class="task-status"
      />
      <ReferenceIcon
        v-else-if="destinationId && !sectionIconComponent"
        :target="destinationId"
        size="m"
        class="icon"
      />
      <component
        :is="sectionIconComponent"
        v-else-if="sectionIconComponent"
        class="icon"
      />
      <Avatar v-else-if="avatar" :personish="avatar" size="small" />
      <span
        v-else-if="priorityColor"
        class="priority-mark"
        :class="[`option-color-${priorityColor}`]"
        >!</span
      >
      <span
        v-else-if="labelColor"
        class="label-dot"
        :class="[`option-color-${labelColor}`]"
      />
      <span class="label">
        <span v-if="textIsHtml" class="text" v-text="baseText" />
        <span v-else class="text">{{ baseText }}</span>
        <template v-if="labelContext">
          <span class="context-separator">·</span>
          <span class="context">{{ labelContext }}</span>
        </template>
      </span>
    </div>
    <div v-if="!noCompletionHelpers" class="right-text">
      <span v-if="date" class="helper-date">{{ date }}</span>
      <CalendarColorBar
        v-if="calendarColorBar && isEvent"
        :color="calendarColorBar"
      />
      <slot name="helpers" :completion="completion">
        <ConsoleCompletionHelpers
          :completion="completion"
          :class="[
            calendarColorBar && isEvent ? `calendar-${calendarColorBar}` : '',
            { selected },
          ]"
        />
      </slot>
    </div>
  </li>
</template>

<script lang="ts">
import { computedAsync } from "@vueuse/core";
import { format } from "date-fns";
import { match } from "ts-pattern";
import { computed, defineComponent, PropType, toRefs } from "vue";

import { RoutineMandateCompletion } from "@ctrl/controller-rpc";

import { getColor, resolveLabelColor } from "@/components/color";
import ConsoleCompletionHelpers from "@/components/ConsoleCompletionHelpers.vue";
import { getDaytime } from "@/components/dates";
import Avatar, { Personish } from "@/components/elements/Avatar.vue";
import CalendarColorBar from "@/components/elements/CalendarColorBar.vue";
import CompleteIcon from "@/components/icons/CompleteIcon.vue";
import H1Icon from "@/components/icons/H1Icon.vue";
import H2Icon from "@/components/icons/H2Icon.vue";
import H3Icon from "@/components/icons/H3Icon.vue";
import ReferenceIcon from "@/components/icons/ReferenceIcon.vue";
import { getReferenceContextLabel } from "@/components/reference-context";
import TaskCheckbox, {
  type TupleStatus,
} from "@/components/task/TaskCheckbox.vue";
import TypeIcon from "@/components/TypeIcon.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

import * as inject from "./inject";

export default defineComponent({
  components: {
    Avatar,
    TypeIcon,
    ConsoleCompletionHelpers,
    CalendarColorBar,
    CompleteIcon,
    ReferenceIcon,
    TaskCheckbox,
  },
  props: {
    completion: {
      type: Object as PropType<RoutineMandateCompletion>,
      required: true,
    },
    noCompletionHelpers: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    textIsHtml: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    click: (_payload: MouseEvent) => true,
    mouseover: (_payload: MouseEvent) => true,
  },
  async setup(props, { emit }) {
    const controller = inject.controller();
    const profile = inject.profile();
    const browserStore = useBrowserStore(profile.value.user.accountId);
    const locale = inject.locale();
    const workspaceDatabases = inject.workspaceDatabases();
    const { completion } = toRefs(props);
    const completionValue = completion.value.value;
    const baseText = computed(() => completion.value.text);

    const isEvent = computed(
      () =>
        completionValue.type === "destination" &&
        completionValue.value.type === "document" &&
        completionValue.value.id.kind === "event"
    );
    const isObject = computed(
      () =>
        completionValue.type === "destination" &&
        completionValue.value.type === "document" &&
        completionValue.value.id.kind === "object"
    );
    const destinationId = computed(() => {
      if (completionValue.type !== "destination") return null;
      return match(completionValue.value)
        .with({ type: "document" }, ({ id }) => id)
        .otherwise(() => null);
    });
    const sectionIconComponent = computed(() => {
      if (completionValue.type !== "destination") return null;
      return match(completionValue.value)
        .with({ type: "document", id: { kind: "page" } }, ({ section }) => {
          if (!section) return null;
          return match(section[1])
            .with(1, () => H1Icon)
            .with(2, () => H2Icon)
            .with(3, () => H3Icon)
            .otherwise(() => null);
        })
        .otherwise(() => null);
    });
    const [event, objectIdRaw, task] = await (() => {
      const event = bindAsync(completion, async (completion) => {
        return await match(completion.value.value)
          .with(
            { type: "document", id: { kind: "event" } },
            async ({ id: { id } }) => await controller.event.get(id)
          )
          .otherwise(() => releasableRef(null));
      });
      const objectIdRaw = bindAsync(completion, async (completion) => {
        return await match(completion.value.value)
          .with(
            { type: "document", id: { kind: "object" } },
            async ({ id: { id } }) => {
              const parsed = await controller.graph.object.id.parse(id);
              return releasableRef(parsed);
            }
          )
          .otherwise(() => releasableRef(null));
      });
      const task = bindAsync(completion, async (completion) => {
        return await match(completion.value.value)
          .with(
            { type: "document", id: { kind: "task" } },
            async ({ id: { id } }) => await controller.task.get(id)
          )
          .otherwise(() => releasableRef(null));
      });
      const releaser = useReleaser();
      releaser.add(event);
      releaser.add(objectIdRaw);
      releaser.add(task);
      return Promise.all([event, objectIdRaw, task]);
    })();
    const labelContext = computedAsync<string | null>(async () => {
      const completionValue = completion.value.value;
      if (
        completionValue.type !== "destination" ||
        completionValue.value.type !== "document"
      ) {
        return null;
      }

      return getReferenceContextLabel(
        controller,
        profile.value,
        completionValue.value.id
      );
    }, null);

    // The type an `as <Type>` completion selects for the object to create.
    const asType = computed(() =>
      completionValue.type === "type" ? completionValue.value : null
    );
    // The type of the object a destination completion refers to.
    const objectType = computed(() => {
      if (!isObject.value) return null;
      const raw = objectIdRaw.value;
      if (!raw) return null;
      const workspaceId = raw.workspace_id;
      const tableId = raw.table_id;
      if (!workspaceId || !tableId) return null;
      const dict = workspaceDatabases.value?.[workspaceId];
      if (!dict) return null;
      return Object.values(dict).find((d) => d.id === tableId) ?? null;
    });
    // The status of the task a destination completion refers to.
    const taskStatus = computed<TupleStatus | null>(() =>
      task.value
        ? { completed: task.value.completed, ignored: task.value.ignored }
        : null
    );
    const calendarColorBar = await match(completionValue.value)
      .with({ type: "calendar" }, async (calendar) => {
        const c = await controller.calendar.get(calendar.id);
        c.release();
        return c.value ? c.value.color : null;
      })
      .with({ type: "document", id: { kind: "event" } }, async () => {
        const id = event.value?.calendar;
        if (id) {
          const c = await controller.calendar.get(id);
          c.release();
          return c.value ? c.value.color : null;
        }
      })
      .otherwise(() => null);
    const avatar = computed<Personish | null>(() => {
      if (completionValue.type === "participant") {
        const personish: Personish = {
          email:
            typeof completionValue.value === "object"
              ? completionValue.value.email
              : completionValue.value,
          kind: "attendee",
        };
        return personish;
      } else {
        return null;
      }
    });
    const priorityColor = computed(() => {
      if (completionValue.type === "priority") {
        return getColor(completionValue.value.name);
      }
      return null;
    });
    const labelColor = computed(() => {
      if (completionValue.type === "label") {
        return resolveLabelColor(
          browserStore,
          completionValue.value.id,
          completionValue.value.name
        );
      }
      return null;
    });

    const date = computed(() => {
      if (completionValue.type !== "destination") return null;
      return match(completionValue.value)
        .with({ type: "document", id: { kind: "event" } }, () => {
          return event.value
            ? `${format(
                event.value.start,
                "EEE, MMM	do"
              )} • ${locale.value.formatDaytime(
                getDaytime(event.value.start)
              )} → ${locale.value.formatDaytime(getDaytime(event.value.end))}`
            : null;
        })
        .otherwise(() => null);
    });

    const onClick = (payload: MouseEvent) => emit("click", payload);
    const onMouseover = (payload: MouseEvent) => emit("mouseover", payload);
    return {
      avatar,
      calendarColorBar,
      date,
      destinationId,
      sectionIconComponent,
      isEvent,
      objectType,
      asType,
      onClick,
      onMouseover,
      labelColor,
      priorityColor,
      baseText,
      labelContext,
      taskStatus,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/console.scss" as *;

.console-completion {
  padding: $paddingCompletion;
  cursor: default;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  column-gap: 20px;

  .left-text {
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    column-gap: 8px;

    .calendar-bar {
      margin-inline-start: 7px;
      margin-inline-end: 7px;
    }

    // The task checkbox sizes its own check and cross icons.
    :deep(svg:not(.recurring-svg, .task-status svg)) {
      height: 16px;
      width: 16px;
    }

    :deep(.task-icon > svg) {
      height: 18px;
      width: 18px;
    }

    .icon {
      display: flex;
      flex-shrink: 0;

      :deep(path.stroke) {
        @include themed() {
          stroke: t($grey-50);
        }
      }

      :deep(path.fill) {
        @include themed() {
          fill: t($grey-50);
        }

        // Ensure white checkmark is preserved when isWhite prop is set
        &.isWhite {
          @include themed() {
            fill: t($bg);
          }
        }
      }
    }

    // BaseCheckbox stops click propagation: let clicks through to the
    // completion, which its host selects.
    .task-status {
      flex-shrink: 0;
      pointer-events: none;
    }

    .object-emoji {
      width: 16px;
      height: 16px;
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;

      // TypeIcon's class lands on its root, so an emoji root carries both
      // `emoji` and `object-emoji`.
      &.emoji {
        font-size: 16px;
        line-height: 16px;
        // Emoji fonts tend to sit slightly low vs SVG icons.
        transform: translateY(-1px);
      }
    }

    .priority-mark {
      flex-shrink: 0;
      font-size: 14px;
      font-weight: 600;
      &.option-color-a {
        color: $calendar-a-50;
      }
      &.option-color-b {
        color: $calendar-b-50;
      }
      &.option-color-c {
        color: $calendar-c-50;
      }
      &.option-color-d {
        color: $calendar-d-50;
      }
      &.option-color-e {
        color: $calendar-e-50;
      }
      &.option-color-f {
        color: $calendar-f-50;
      }
      &.option-color-g {
        color: $calendar-g-50;
      }
      &.option-color-h {
        color: $calendar-h-50;
      }
      &.option-color-i {
        color: $calendar-i-50;
      }
    }

    .label-dot {
      flex-shrink: 0;
      height: 6px;
      width: 6px;
      border-radius: 3px;
      @include generate-calendar-mid-colors;
    }

    .label {
      display: flex;
      align-items: center;
      min-width: 0;
      column-gap: 4px;
    }

    .text,
    .context {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .text {
      @extend %font-body-medium;

      @include themed() {
        color: t($grey-70);
      }
    }

    .context-separator,
    .context {
      @extend %font-body-medium;
      flex-shrink: 0;

      @include themed() {
        color: t($grey-50);
      }
    }

    .context {
      min-width: 0;
      flex-shrink: 1;
    }
  }

  .right-text {
    display: flex;
    align-items: center;
    min-width: 0;
    flex-shrink: 2;
    column-gap: 6px;
    @extend %font-caption-regular;

    @include themed() {
      color: t($grey-50);
    }

    .helper-date {
      overflow: hidden;
    }

    .helper-text,
    .helper-date {
      @extend %font-caption-regular;
      white-space: nowrap;
      text-overflow: ellipsis;

      @include themed() {
        color: t($grey-50);
      }

      &.calendar-a {
        color: $calendar-a-50;
      }

      &.calendar-b {
        color: $calendar-b-50;
      }

      &.calendar-c {
        color: $calendar-c-50;
      }

      &.calendar-d {
        color: $calendar-d-50;
      }

      &.calendar-e {
        color: $calendar-e-50;
      }

      &.calendar-f {
        color: $calendar-f-50;
      }

      &.calendar-g {
        color: $calendar-g-50;
      }

      &.calendar-h {
        color: $calendar-h-50;
      }

      &.calendar-i {
        color: $calendar-i-50;
      }
    }
  }

  &.selected {
    .left-text .text {
      @include themed() {
        color: t($primary);
      }
    }
  }
}
</style>
